import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 30_000; // Beckn TTL PT30S — show "taking longer" past this.

/**
 * Generic poller for an async Beckn draft (metro tickets or on-demand rides).
 * Polls the draft for a transaction every 1.5s, stopping when the current step
 * completes or errors. Surfaces a `timedOut` flag when the step has not settled
 * within `timeoutMs` (default ~30s). A booking reuses one txn across steps, so each
 * step (signalled by `step` changing) gets its own budget.
 *
 * The budget is a client-side backstop, not the authority: the server always drives a
 * discovery to a terminal status of its own accord. Giving up first is therefore a way
 * to miss a result that did arrive.
 *
 * Both `TransitDraft` and `RideDraft` share the exact `step`/`status` contract,
 * so metro and rides each wrap this with their own draftFn + queryKey factory.
 */
export function useBeckonDraft<T extends { step: string; status: string }>(
  transactionId: string | null,
  draftFn: (txnId: string) => Promise<T>,
  queryKey: (txnId: string) => readonly unknown[],
  // How long this step may stay un-settled before we stop polling and flag `timedOut`.
  // Defaults to the Beckn TTL, which suits a step answered by exactly one callback
  // (select/init/confirm). A step the server fulfils across MANY callbacks — a paginated
  // catalog discovery — must pass a longer budget: see DISCOVERY_POLL_TIMEOUT_MS.
  timeoutMs: number = POLL_TIMEOUT_MS,
  // Some paid flows must keep observing the server after the warning threshold. In particular,
  // TRV11 technical cancellation starts only after a late on_confirm and completes asynchronously;
  // stopping here would make its refund dialog depend on the rider repeatedly pressing Retry.
  continueAfterTimeout = false
) {
  const [timedOut, setTimedOut] = useState(false);
  const [resumeVersion, setResumeVersion] = useState(0);
  const startRef = useRef<number | null>(null);
  const resumeFromRef = useRef<{ step: string; status: string } | null>(null);
  const activeTransactionRef = useRef<string | null>(null);

  const query = useQuery({
    queryKey: queryKey(transactionId ?? ''),
    queryFn: () => draftFn(transactionId as string),
    enabled: Boolean(transactionId) && (!timedOut || continueAfterTimeout),
    refetchIntervalInBackground: true,
    refetchInterval: (q) => {
      const d = q.state.data;
      const resumeFrom = resumeFromRef.current;
      if (resumeFrom) {
        const hasAdvanced = d && (d.step !== resumeFrom.step || d.status !== resumeFrom.status);
        const advancedToTerminal =
          hasAdvanced &&
          (d.status === 'completed' || d.status === 'error' || d.status === 'unavailable');
        if (!advancedToTerminal) return POLL_INTERVAL_MS;
        resumeFromRef.current = null;
      }
      // 'unavailable' is terminal too: a city-only discovery whose search TTL elapsed with no serving
      // BPP (nobody serves the city / every responder was dropped). Stop polling — there is nothing more to wait for.
      if (d && (d.status === 'completed' || d.status === 'error' || d.status === 'unavailable'))
        return false;
      return POLL_INTERVAL_MS;
    },
  });

  // A fare search may deliberately reuse the discovery transaction ID. TanStack Query then serves
  // the cached completed discovery draft for the same key and would disable polling before the
  // fare callback arrives. Treat activating that cached transaction as a new async step.
  useEffect(() => {
    if (!transactionId || activeTransactionRef.current === transactionId) return;
    activeTransactionRef.current = transactionId;
    const current = query.data;
    if (
      current &&
      (current.status === 'completed' ||
        current.status === 'error' ||
        current.status === 'unavailable')
    ) {
      resumeFromRef.current = { step: current.step, status: current.status };
      setResumeVersion((version) => version + 1);
    }
  }, [transactionId, query.data]);

  // Restart the timeout clock when a new transaction begins OR a new step starts.
  const step = query.data?.step;
  useEffect(() => {
    if (transactionId) {
      startRef.current = Date.now();
      setTimedOut(false);
    } else {
      startRef.current = null;
    }
  }, [transactionId, step]);

  const settled =
    !resumeFromRef.current &&
    (query.data?.status === 'completed' ||
      query.data?.status === 'error' ||
      query.data?.status === 'unavailable');

  useEffect(() => {
    if (!transactionId || timedOut || settled) return;
    const interval = setInterval(() => {
      if (startRef.current && Date.now() - startRef.current > timeoutMs) {
        setTimedOut(true);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [transactionId, timedOut, settled, resumeVersion, timeoutMs]);

  // Clear a lingering "taking longer" banner once the step settles, even if the
  // `step` string didn't change (e.g. search → search/completed) — otherwise the
  // banner renders over the next screen and the user is stuck re-clicking Retry.
  useEffect(() => {
    if (settled) setTimedOut(false);
  }, [settled]);

  const resumePolling = useCallback(() => {
    const current = query.data;
    resumeFromRef.current = current ? { step: current.step, status: current.status } : null;
    startRef.current = Date.now();
    setTimedOut(false);
    setResumeVersion((version) => version + 1);
    return query.refetch();
  }, [query]);

  return {
    draft: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    timedOut,
    refetch: query.refetch,
    resumePolling,
  };
}
